"use client";

import { emailSchema } from "@repo/api/lib/validation";
import { authClient } from "@repo/auth/client";
import { getAuthErrorMessage } from "@saas/auth/client";
import { AuthHeading } from "@saas/shared/components/AuthWrapper";
import { useForm, useStore } from "@tanstack/react-form";
import { Button } from "@ui/components/button";
import { Field, FieldError, FieldLabel } from "@ui/components/field";
import { Input } from "@ui/components/input";
import { LinkButton } from "@ui/components/link-button";
import { Spinner } from "@ui/components/spinner";
import { ArrowLeftIcon, MailCheckIcon } from "lucide-react";
import { useState } from "react";
import {
	AuthDone,
	AuthError,
	authErrorInput,
	visibleFieldError,
} from "./AuthNotice";

export function ForgotPasswordForm() {
	const [rootError, setRootError] = useState<string | null>(null);
	const [sentTo, setSentTo] = useState<string | null>(null);

	const form = useForm({
		defaultValues: { email: "" },
		onSubmit: async ({ value }) => {
			setRootError(null);
			try {
				const { error } = await authClient.requestPasswordReset({
					email: value.email,
					redirectTo: new URL(
						"/reset-password",
						window.location.origin,
					).toString(),
				});
				if (error) {
					throw error;
				}
				setSentTo(value.email);
			} catch (error) {
				setRootError(getAuthErrorMessage(authErrorInput(error)));
			}
		},
	});

	const isSubmitting = useStore(form.store, (state) => state.isSubmitting);
	const attempted = useStore(
		form.store,
		(state) => state.submissionAttempts > 0,
	);

	if (sentTo) {
		return (
			<AuthDone
				icon={<MailCheckIcon />}
				title="Check your inbox"
				actions={
					<LinkButton to="/login" variant="outline">
						Back to sign in
					</LinkButton>
				}
			>
				If <span className="font-medium text-foreground">{sentTo}</span>{" "}
				has an account, a link to choose a new password is on its way.
			</AuthDone>
		);
	}

	return (
		<div className="flex flex-col">
			<AuthHeading
				title="Reset your password"
				description="We'll email you a reset link."
			/>
			<form
				className="flex flex-col gap-5"
				noValidate
				onSubmit={(event) => {
					event.preventDefault();
					event.stopPropagation();
					form.handleSubmit();
				}}
			>
				<AuthError message={rootError} />
				<form.Field name="email" validators={{ onBlur: emailSchema }}>
					{(field) => {
						const error = visibleFieldError(field, attempted);
						const invalid = !!error;
						return (
							<Field data-invalid={invalid || undefined}>
								<FieldLabel htmlFor="email">
									Work email
								</FieldLabel>
								<Input
									id="email"
									type="email"
									placeholder="you@retailer.com"
									value={field.state.value}
									onChange={(event) =>
										field.handleChange(event.target.value)
									}
									onBlur={field.handleBlur}
									autoComplete="email"
									autoFocus
									aria-invalid={invalid || undefined}
								/>
								{invalid ? <FieldError errors={error} /> : null}
							</Field>
						);
					}}
				</form.Field>
				<Button type="submit" size="lg" disabled={isSubmitting}>
					{isSubmitting ? <Spinner data-icon="inline-start" /> : null}
					Send reset link
				</Button>
				<LinkButton to="/login" variant="ghost" className="self-center">
					<ArrowLeftIcon data-icon="inline-start" />
					Back to sign in
				</LinkButton>
			</form>
		</div>
	);
}
